/**
 * Favorite scents — Phase 1 (browser-only scaffold).
 *
 * - FavoritesStore: synchronous in-memory cache of favorites keyed by product ID.
 *   Backends only hydrate/persist it. Phase 1 ships LocalBackend (localStorage).
 *   A future async backend hydrates later and dispatches `favorites:loaded`.
 * - <favorite-button>: self-hydrating heart toggle. Reads its snapshot from data-*
 *   attributes, so cards injected via innerHTML (recommendations, recently viewed,
 *   quick-buy drawers, facets) work with no extra wiring.
 * - <favorites-page>: controller for sections/main-favorites.liquid (tabs, favorites list).
 * - FeedbackStore + <scent-feedback>: like / "not for me" (+ reason) on purchased scents,
 *   stored in localStorage['theme:scent-feedback'] behind the same backend seam.
 *   Phase 2 target: customer metafield favorites.feedback (json). Records only — no
 *   downstream effect on recommendations or kits.
 *
 * Events (dispatched on document.documentElement, bubbling):
 *   favorites:changed { id, action: 'add' | 'remove' | 'note' | 'reload' }
 *
 * Stashed notes: unfavoriting a scent that has a note keeps the note (stash); favoriting it
 * again brings it back. Guests: localStorage['theme:favorites-stash']. Accounts: the worker
 * keeps favorites.stashed_notes and returns it as state.stash.
 *   feedback:changed  { id, action: 'set' | 'clear' | 'reload' }
 *   favorites:loaded  {}   (reserved for async backends)
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'theme:favorites';
  var STASH_KEY = 'theme:favorites-stash';
  var STASH_MAX = 200;
  var NOTE_MAX = 140;

  // Liquid's `t` filter HTML-escapes ("didn&#39;t"); decode once so textContent renders cleanly.
  var decodedStrings = null;
  function decodeEntities(value) {
    if (typeof value !== 'string' || value.indexOf('&') === -1) return value;
    var el = document.createElement('textarea');
    el.innerHTML = value;
    return el.value;
  }
  // Shopify CDN images: make sure a width is requested so cards never load the full-size original
  function sizedImage(url, width) {
    if (!url || !/cdn\/shop\/|cdn\.shopify\.com\//.test(url)) return url || '';
    if (/[?&]width=/.test(url)) return url;
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + (width || 400);
  }

  function themeStrings() {
    if (decodedStrings) return decodedStrings;
    var raw = (window.themeVariables && window.themeVariables.strings) || {};
    var out = {};
    Object.keys(raw).forEach(function (key) {
      var value = raw[key];
      if (value && typeof value === 'object') {
        out[key] = {};
        Object.keys(value).forEach(function (k) { out[key][k] = decodeEntities(value[k]); });
      } else {
        out[key] = decodeEntities(value);
      }
    });
    decodedStrings = out;
    return out;
  }

  function themeRoutes() {
    return (window.themeVariables && window.themeVariables.routes) || {};
  }

  function isLoggedIn() {
    return !!(window.themeVariables && window.themeVariables.customer && window.themeVariables.customer.loggedIn);
  }

  // Favorites require an account: send the shopper to sign in and bring them back here.
  function requireLogin() {
    var login = themeRoutes().accountLoginUrl || '/account/login';
    var back = location.pathname + location.search + location.hash;
    location.href = login + (login.indexOf('?') === -1 ? '?' : '&') + 'return_url=' + encodeURIComponent(back);
  }

  function dispatch(name, detail) {
    document.documentElement.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: detail || {} }));
  }

  function isIntegerId(value) {
    return /^\d+$/.test(String(value));
  }

  function cleanNote(text) {
    // Plain strings only; strip control characters (keep \n and \t).
    return String(text == null ? '' : text)
      .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '')
      .slice(0, NOTE_MAX);
  }

  function cleanSnapshot(id, raw) {
    if (!raw || typeof raw !== 'object') return null;
    var snapshot = {
      id: Number(id),
      handle: typeof raw.handle === 'string' ? raw.handle : '',
      title: typeof raw.title === 'string' ? raw.title : '',
      image: typeof raw.image === 'string' ? raw.image : '',
      scent_notes: typeof raw.scent_notes === 'string' ? raw.scent_notes : '',
      scent_type: Array.isArray(raw.scent_type) ? raw.scent_type.filter(function (t) { return typeof t === 'string'; }) : [],
      added_at: typeof raw.added_at === 'number' ? raw.added_at : Date.now()
    };
    if (typeof raw.note === 'string' && raw.note !== '') snapshot.note = cleanNote(raw.note);
    return snapshot;
  }

  /* ------------------------------------------------------------------ */
  /* Backend seam: { load, add, remove, setNote }                        */
  /* ------------------------------------------------------------------ */

  var LocalBackend = {
    persistent: true,

    load: function () {
      var map = new Map();
      try {
        var parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          Object.keys(parsed).forEach(function (key) {
            if (!isIntegerId(key)) return; // tampered / foreign keys are ignored
            var snapshot = cleanSnapshot(key, parsed[key]);
            if (snapshot) map.set(String(snapshot.id), snapshot);
          });
        }
      } catch (e) {
        // Safari private mode / disabled storage: stay in-memory for the session
        this.persistent = false;
      }
      return map;
    },

    _write: function (map) {
      try {
        var out = {};
        map.forEach(function (snapshot, key) { out[key] = snapshot; });
        localStorage.setItem(STORAGE_KEY, JSON.stringify(out));
        this.persistent = true;
        return true;
      } catch (e) {
        this.persistent = false;
        return false;
      }
    },

    add: function (map) { return this._write(map); },
    remove: function (map) { return this._write(map); },
    setNote: function (map) { return this._write(map); }
  };

  // Notes of unfavorited scents, keyed by product ID: { note, title, removed_at }
  function cleanStashMap(raw) {
    var map = new Map();
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return map;
    Object.keys(raw).forEach(function (key) {
      var entry = raw[key];
      if (!isIntegerId(key) || !entry || typeof entry.note !== 'string') return;
      var note = cleanNote(entry.note);
      if (!note) return;
      map.set(String(key), { note: note, title: typeof entry.title === 'string' ? entry.title : '', removed_at: typeof entry.removed_at === 'number' ? entry.removed_at : Date.now() });
    });
    return map;
  }

  var LocalStash = {
    load: function () {
      try { return cleanStashMap(JSON.parse(localStorage.getItem(STASH_KEY) || '{}')); } catch (e) { return new Map(); }
    },
    write: function (map) {
      try {
        var out = {};
        Array.from(map.keys())
          .sort(function (a, b) { return map.get(b).removed_at - map.get(a).removed_at; })
          .slice(0, STASH_MAX)
          .forEach(function (key) { out[key] = map.get(key); });
        if (Object.keys(out).length) localStorage.setItem(STASH_KEY, JSON.stringify(out));
        else localStorage.removeItem(STASH_KEY);
      } catch (e) { /* storage unavailable: the stash lasts for this page view */ }
    }
  };

  /* ------------------------------------------------------------------ */
  /* ProxyBackend — customer metafields via the favorites app proxy      */
  /*   Hydrates asynchronously (hearts show aria-busy until favorites:loaded), */
  /*   writes optimistically and coalesces ops into one POST at a time.  */
  /* ------------------------------------------------------------------ */

  var ProxyBackend = {
    persistent: true,
    queue: [],
    flushing: false,
    hydrated: false,

    url: function () {
      var settings = (window.themeVariables && window.themeVariables.settings) || {};
      return (settings.favoritesProxyPath || '/apps/favorites').replace(/\/$/, '') + '/state';
    },

    // Sync part: nothing yet. Async hydration follows; guests stay empty (hearts redirect to login).
    load: function () {
      if (isLoggedIn()) this.hydrate();
      return new Map();
    },

    hydrate: async function () {
      try {
        var response = await fetch(this.url(), { headers: { 'Accept': 'application/json' }, credentials: 'same-origin' });
        if (!response.ok) throw new Error('state ' + response.status);
        var state = await response.json();
        this.applyState(state);
        this.hydrated = true;
        FavoritesStore.loaded = true;
        dispatch('favorites:changed', { id: null, action: 'reload' });
        dispatch('feedback:changed', { id: null, action: 'reload' });
        dispatch('favorites:loaded', {});
        this.mergeLocal();
      } catch (e) {
        this.persistent = false;
        FavoritesStore.loaded = true; // stop showing busy; hearts work in-memory for this page view
        dispatch('favorites:loaded', {});
      }
    },

    applyState: function (state) {
      var favorites = new Map();
      Object.keys(state.favorites || {}).forEach(function (key) {
        if (!isIntegerId(key)) return;
        var snapshot = cleanSnapshot(key, state.favorites[key]);
        if (snapshot) favorites.set(String(snapshot.id), snapshot);
      });
      FavoritesStore.items = favorites;
      FavoritesStore.stash = cleanStashMap(state.stash);
      var feedback = new Map();
      Object.keys(state.feedback || {}).forEach(function (key) {
        if (!isIntegerId(key)) return;
        var entry = cleanFeedback(key, state.feedback[key]);
        if (entry) feedback.set(String(key), entry);
      });
      FeedbackStore.items = feedback;
    },

    // One-time guest → account merge of anything still in localStorage, then clear it
    mergeLocal: function () {
      var localFavorites = LocalBackend.load();
      var localFeedback = FeedbackStore.loadLocal();
      var localStash = LocalStash.load();
      if (!localFavorites.size && !localFeedback.size && !localStash.size) return;
      var favorites = {}, feedback = {}, stash = {};
      localFavorites.forEach(function (snapshot, key) { favorites[key] = snapshot; });
      localFeedback.forEach(function (entry, key) { feedback[key] = entry; });
      localStash.forEach(function (entry, key) { stash[key] = entry; });
      this.enqueue({ type: 'merge', favorites: favorites, feedback: feedback, stash: stash }, function (ok) {
        if (!ok) return;
        try { localStorage.removeItem(STORAGE_KEY); localStorage.removeItem(FEEDBACK_KEY); localStorage.removeItem(STASH_KEY); } catch (e) { /* ignore */ }
      });
    },

    add: function (map, snapshot) { this.enqueue({ type: 'add', snapshot: snapshot }); return true; },
    remove: function (map, removed) { this.enqueue({ type: 'remove', id: removed.id }); return true; },
    setNote: function (map, snapshot) { this.enqueue({ type: 'note', id: snapshot.id, note: snapshot.note || '' }); return true; },
    setFeedback: function (id, entry) { this.enqueue({ type: 'feedback', id: Number(id), entry: entry }); return true; },

    enqueue: function (op, done) {
      this.queue.push({ op: op, done: done });
      var self = this;
      if (!this.flushTimer) this.flushTimer = setTimeout(function () { self.flushTimer = null; self.flush(); }, 150);
    },

    flush: async function () {
      if (this.flushing || !this.queue.length) return;
      this.flushing = true;
      var batch = this.queue.splice(0, 50);
      var ok = false, full = false;
      try {
        var response = await fetch(this.url(), {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ ops: batch.map(function (b) { return b.op; }) })
        });
        if (response.status === 409) full = true;
        if (response.ok) {
          this.applyState(await response.json()); // server state wins (cap, cleaning, other devices)
          ok = true;
        }
      } catch (e) { /* network */ }
      this.flushing = false;
      batch.forEach(function (b) { if (b.done) b.done(ok); });
      if (!ok) {
        // Revert optimistic state by re-reading from the server; tell the shopper
        this.persistent = false;
        announce(full ? (themeStrings().favoritesFull || 'Your scent library is full') : (themeStrings().favoritesSyncError || "Couldn't sync"));
        this.hydrate();
      } else {
        dispatch('favorites:changed', { id: null, action: 'reload' });
        dispatch('feedback:changed', { id: null, action: 'reload' });
      }
      if (this.queue.length) this.flush();
    }
  };

  function selectBackend() {
    var settings = (window.themeVariables && window.themeVariables.settings) || {};
    return settings.favoritesBackend === 'proxy' ? ProxyBackend : LocalBackend;
  }

  /* ------------------------------------------------------------------ */
  /* FavoritesStore singleton                                            */
  /* ------------------------------------------------------------------ */

  var FavoritesStore = {
    backend: LocalBackend,
    items: new Map(),
    stash: new Map(),
    loaded: false,

    init: function () {
      this.backend = selectBackend();
      this.items = this.backend.load();
      if (this.backend === LocalBackend) this.stash = LocalStash.load();
      this.loaded = this.backend !== ProxyBackend; // proxy flips this once hydrated

      window.addEventListener('storage', function (event) {
        if (FavoritesStore.backend === LocalBackend && (event.key === STORAGE_KEY || event.key === null)) FavoritesStore.reload();
      });
      window.addEventListener('pageshow', function (event) {
        if (event.persisted) FavoritesStore.reload();
      });
    },

    reload: function () {
      if (this.backend === ProxyBackend) { ProxyBackend.hydrate(); return; }
      this.items = this.backend.load();
      this.stash = LocalStash.load();
      dispatch('favorites:changed', { id: null, action: 'reload' });
    },

    get persistent() {
      return this.backend.persistent !== false;
    },

    has: function (id) {
      return this.items.has(String(id));
    },

    get: function (id) {
      return this.items.get(String(id)) || null;
    },

    list: function () {
      return Array.from(this.items.values()).sort(function (a, b) { return b.added_at - a.added_at; });
    },

    count: function () {
      return this.items.size;
    },

    add: function (snapshot) {
      if (!snapshot || !isIntegerId(snapshot.id)) return false;
      var clean = cleanSnapshot(snapshot.id, snapshot);
      // Favorited again: bring back the note it had when it was unfavorited (an undo already carries it)
      var stashed = this.stash.get(String(clean.id));
      if (stashed) {
        if (!clean.note) clean.note = stashed.note;
        this.stash.delete(String(clean.id));
        if (this.backend === LocalBackend) LocalStash.write(this.stash);
      }
      this.items.set(String(clean.id), clean);
      var ok = this.backend.add(this.items, clean);
      dispatch('favorites:changed', { id: clean.id, action: 'add' });
      return ok;
    },

    remove: function (id) {
      var key = String(id);
      if (!this.items.has(key)) return false;
      var removed = this.items.get(key);
      this.items.delete(key);
      // Keep the note so it comes back if this scent is favorited again (the proxy stashes server-side too)
      if (removed.note) {
        this.stash.set(key, { note: removed.note, title: removed.title || '', removed_at: Date.now() });
        if (this.backend === LocalBackend) LocalStash.write(this.stash);
      }
      var ok = this.backend.remove(this.items, removed);
      dispatch('favorites:changed', { id: Number(id), action: 'remove' });
      return ok;
    },

    setNote: function (id, text) {
      var key = String(id);
      var snapshot = this.items.get(key);
      if (!snapshot) return false;
      var note = cleanNote(text);
      if (note === '') delete snapshot.note; else snapshot.note = note;
      var ok = this.backend.setNote(this.items, snapshot);
      dispatch('favorites:changed', { id: Number(id), action: 'note' });
      return ok;
    }
  };

  FavoritesStore.init();
  window.FavoritesStore = FavoritesStore;

  /* ------------------------------------------------------------------ */
  /* FeedbackStore — { "<productId>": { rating: 'like'|'dislike', reason?, comment?, at } } */
  /* ------------------------------------------------------------------ */

  var FEEDBACK_KEY = 'theme:scent-feedback';
  var FEEDBACK_REASONS = ['too_strong', 'too_weak', 'scent', 'faded', 'other'];

  function cleanFeedback(id, raw) {
    if (!raw || typeof raw !== 'object') return null;
    if (raw.rating !== 'like' && raw.rating !== 'dislike') return null;
    var entry = { rating: raw.rating, at: typeof raw.at === 'number' ? raw.at : Date.now() };
    if (typeof raw.title === 'string' && raw.title) entry.title = cleanNote(raw.title); // so Klaviyo can name non-favorited scents
    if (raw.rating === 'dislike') {
      if (FEEDBACK_REASONS.indexOf(raw.reason) !== -1) entry.reason = raw.reason;
      if (typeof raw.comment === 'string' && raw.comment !== '') entry.comment = cleanNote(raw.comment);
    }
    return entry;
  }

  var FeedbackStore = {
    persistent: true,
    items: new Map(),

    init: function () {
      this.proxy = selectBackend() === ProxyBackend;
      this.items = this.proxy ? new Map() : this.loadLocal(); // proxy hydrates alongside favorites
      window.addEventListener('storage', function (event) {
        if (!FeedbackStore.proxy && (event.key === FEEDBACK_KEY || event.key === null)) FeedbackStore.reload();
      });
      window.addEventListener('pageshow', function (event) {
        if (event.persisted && !FeedbackStore.proxy) FeedbackStore.reload();
      });
    },

    load: function () { return this.loadLocal(); },

    loadLocal: function () {
      var map = new Map();
      try {
        var parsed = JSON.parse(localStorage.getItem(FEEDBACK_KEY) || '{}');
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          Object.keys(parsed).forEach(function (key) {
            if (!isIntegerId(key)) return;
            var entry = cleanFeedback(key, parsed[key]);
            if (entry) map.set(String(key), entry);
          });
        }
      } catch (e) {
        this.persistent = false;
      }
      return map;
    },

    _write: function (id, entry) {
      if (this.proxy) return ProxyBackend.setFeedback(id, entry);
      try {
        var out = {};
        this.items.forEach(function (entry, key) { out[key] = entry; });
        localStorage.setItem(FEEDBACK_KEY, JSON.stringify(out));
        this.persistent = true;
        return true;
      } catch (e) {
        this.persistent = false;
        return false;
      }
    },

    reload: function () {
      if (this.proxy) return;
      this.items = this.loadLocal();
      dispatch('feedback:changed', { id: null, action: 'reload' });
    },

    get: function (id) { return this.items.get(String(id)) || null; },

    set: function (id, entry) {
      if (!isIntegerId(id)) return false;
      var clean = cleanFeedback(id, entry);
      if (!clean) return false;
      this.items.set(String(id), clean);
      var ok = this._write(id, clean);
      dispatch('feedback:changed', { id: Number(id), action: 'set' });
      return ok;
    },

    clear: function (id) {
      if (!this.items.has(String(id))) return true;
      this.items.delete(String(id));
      var ok = this._write(id, null);
      dispatch('feedback:changed', { id: Number(id), action: 'clear' });
      return ok;
    }
  };

  FeedbackStore.init();
  window.FeedbackStore = FeedbackStore;

  /* ------------------------------------------------------------------ */
  /* Shared aria-live announcer                                          */
  /* ------------------------------------------------------------------ */

  var liveRegion = null;
  function announce(message) {
    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.className = 'visually-hidden';
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('aria-atomic', 'true');
      document.body.appendChild(liveRegion);
    }
    liveRegion.textContent = '';
    // Force a change so identical consecutive messages are re-announced
    setTimeout(function () { liveRegion.textContent = message; }, 30);
  }

  /* ------------------------------------------------------------------ */
  /* <favorite-button>                                                   */
  /* ------------------------------------------------------------------ */

  class FavoriteButton extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      if (!this.button) return;

      this.productId = this.getAttribute('data-product-id');
      if (!isIntegerId(this.productId)) return;

      this.labelEl = this.querySelector('[data-favorite-label]');

      if (!this._bound) {
        this._bound = true;
        this._onClick = this._onClick.bind(this);
        this._onChanged = this._onChanged.bind(this);
        this.button.addEventListener('click', this._onClick);
      }
      document.documentElement.addEventListener('favorites:changed', this._onChanged);
      document.documentElement.addEventListener('favorites:loaded', this._onChanged);

      if (!FavoritesStore.loaded) this.button.setAttribute('aria-busy', 'true');
      this.render();
    }

    disconnectedCallback() {
      document.documentElement.removeEventListener('favorites:changed', this._onChanged);
      document.documentElement.removeEventListener('favorites:loaded', this._onChanged);
    }

    get snapshot() {
      var types = (this.getAttribute('data-scent-type') || '').split(',').map(function (t) { return t.trim(); }).filter(Boolean);
      return {
        id: Number(this.productId),
        handle: this.getAttribute('data-handle') || '',
        title: this.getAttribute('data-title') || '',
        image: this.getAttribute('data-image') || '',
        scent_notes: this.getAttribute('data-scent-notes') || '',
        scent_type: types,
        added_at: Date.now()
      };
    }

    _onChanged(event) {
      var id = event.detail && event.detail.id;
      if (id == null || String(id) === String(this.productId)) this.render();
    }

    _onClick(event) {
      event.preventDefault();
      event.stopPropagation();
      if (window.Shopify && window.Shopify.designMode) return;
      if (!isLoggedIn()) { requireLogin(); return; }

      var strings = themeStrings();
      if (FavoritesStore.has(this.productId)) {
        FavoritesStore.remove(this.productId);
        announce(strings.favoritesRemoved || 'Removed from favorites');
      } else {
        FavoritesStore.add(this.snapshot);
        announce(strings.favoritesAdded || 'Added to favorites');
      }
    }

    render() {
      var active = FavoritesStore.has(this.productId);
      var strings = themeStrings();
      var title = this.getAttribute('data-title') || '';

      if (FavoritesStore.loaded) this.button.removeAttribute('aria-busy');
      this.button.setAttribute('aria-pressed', active ? 'true' : 'false');
      this.button.classList.toggle('is-active', active);
      this.classList.toggle('is-active', active);

      var template = active
        ? (strings.favoritesRemoveLabel || 'Remove @title@ from favorites')
        : (strings.favoritesSaveLabel || 'Save @title@ to favorites');
      this.button.setAttribute('aria-label', template.replace('@title@', title).trim());

      if (this.labelEl) {
        this.labelEl.textContent = active
          ? (strings.favoritesSavedText || 'Saved')
          : (strings.favoritesSaveText || 'Save to favorites');
      }
    }
  }

  if (!window.customElements.get('favorite-button')) {
    window.customElements.define('favorite-button', FavoriteButton);
  }

  // Retired PDPs: theme.js resets the disabled ATC text to "Sold out" on variant change; keep "Retired".
  document.documentElement.addEventListener('variant:changed', function () {
    if (!document.querySelector('[data-product-retired]')) return;
    var retired = themeStrings().productFormRetired;
    if (!retired) return;
    document.querySelectorAll('[data-product-add-to-cart-button][disabled]').forEach(function (button) {
      // theme.js writes the label into firstElementChild for loader buttons; keep that structure intact
      var target = button.getAttribute('is') === 'loader-button' && button.firstElementChild ? button.firstElementChild : button;
      if (target.textContent.trim() === (themeStrings().productFormSoldOut || '').trim()) target.textContent = retired;
    });
  });

  // Quick-buy + Skio: the plan picker renders in a shadow root, and Focal's focus trap treats
  // clicks inside it as "outside" and closes the drawer/popover. The trap whitelists targets whose
  // aria-controls names the panel, so tag the picker host once the quick-buy content has loaded.
  document.addEventListener('openable-element:load:end', function (event) {
    var panel = event.target;
    if (!panel || !/^quick-buy-(drawer|popover)$/i.test(panel.tagName) || !panel.id) return;
    panel.querySelectorAll('skio-plan-picker').forEach(function (picker) {
      picker.setAttribute('aria-controls', panel.id);
    });
  }, true);

  /* ------------------------------------------------------------------ */
  /* NoteDialog — one shared <dialog> per page (snippets/favorites-note-dialog.liquid) */
  /* ------------------------------------------------------------------ */

  var NoteDialog = {
    el: null,

    init: function () {
      if (this.el) return this.el;
      var dialog = document.querySelector('[data-fav-note-dialog]');
      if (!dialog || typeof dialog.showModal !== 'function') return null;
      this.el = dialog;
      var self = this;
      var textarea = dialog.querySelector('textarea');
      var counter = dialog.querySelector('[data-fav-note-counter]');
      textarea.maxLength = NOTE_MAX;

      this.updateCounter = function () {
        if (counter) counter.textContent = textarea.value.length + '/' + NOTE_MAX;
      };
      textarea.addEventListener('input', this.updateCounter);

      dialog.querySelectorAll('[data-fav-note-cancel]').forEach(function (button) {
        button.addEventListener('click', function () { dialog.close(); });
      });
      dialog.querySelector('[data-fav-note-save]').addEventListener('click', function () {
        if (self.save()) dialog.close();
      });
      textarea.addEventListener('keydown', function (event) {
        if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
          event.preventDefault();
          if (self.save()) dialog.close();
        }
      });
      dialog.addEventListener('click', function (event) {
        if (event.target === dialog) dialog.close(); // backdrop
      });
      dialog.addEventListener('close', function () {
        var opener = self.opener;
        self.opener = null;
        delete dialog.dataset.productId;
        if (opener && opener.isConnected) opener.focus();
      });
      return dialog;
    },

    open: function (productId, opener) {
      var dialog = this.init();
      var item = FavoritesStore.get(productId);
      if (!dialog || !item) return;
      this.opener = opener || null;
      dialog.dataset.productId = String(item.id);
      dialog.querySelector('[data-fav-note-title]').textContent = item.title;
      var textarea = dialog.querySelector('textarea');
      textarea.value = item.note || '';
      this.updateCounter();
      var error = dialog.querySelector('[data-fav-note-error]');
      if (error) error.hidden = true;
      dialog.showModal();
      textarea.focus();
      textarea.setSelectionRange(textarea.value.length, textarea.value.length);
    },

    save: function () {
      var dialog = this.el;
      var id = dialog.dataset.productId;
      if (!id) return true;
      var ok = FavoritesStore.setNote(id, dialog.querySelector('textarea').value);
      var error = dialog.querySelector('[data-fav-note-error]');
      if (error) error.hidden = ok;
      if (ok) announce(themeStrings().favoritesNoteSaved || 'Note saved');
      return ok;
    }
  };

  // Fills a note preview element + its toggle button from the store
  function renderNotePreview(productId, previewEl, toggleEl) {
    var strings = themeStrings();
    var item = FavoritesStore.get(productId);
    var note = item && item.note;
    if (previewEl) {
      previewEl.textContent = note || '';
      previewEl.hidden = !note;
    }
    if (toggleEl) {
      toggleEl.textContent = note ? (strings.favoritesEditNote || 'Edit note') : (strings.favoritesAddNote || 'Add a note');
    }
  }

  /* ------------------------------------------------------------------ */
  /* <favorite-note> — PDP: read-only display of the customer's note, if one exists */
  /* ------------------------------------------------------------------ */

  class FavoriteNote extends HTMLElement {
    connectedCallback() {
      this.productId = this.getAttribute('data-product-id');
      if (!isIntegerId(this.productId)) return;
      this.preview = this.querySelector('[data-fav-note-preview]');
      this.toggle = this.querySelector('[data-fav-note-toggle]');
      if (!this._bound && this.toggle) {
        this._bound = true;
        var self = this;
        this.toggle.addEventListener('click', function () { NoteDialog.open(self.productId, self.toggle); });
      }
      this._onChanged = this._onChanged.bind(this);
      document.documentElement.addEventListener('favorites:changed', this._onChanged);
      this.render();
    }

    disconnectedCallback() {
      document.documentElement.removeEventListener('favorites:changed', this._onChanged);
    }

    _onChanged(event) {
      var id = event.detail && event.detail.id;
      if (id == null || String(id) === String(this.productId)) this.render();
    }

    render() {
      var item = FavoritesStore.get(this.productId);
      var hasNote = !!(item && item.note);
      this.hidden = !hasNote;
      if (hasNote) renderNotePreview(this.productId, this.preview, this.toggle);
    }
  }

  if (!window.customElements.get('favorite-note')) {
    window.customElements.define('favorite-note', FavoriteNote);
  }

  /* ------------------------------------------------------------------ */
  /* FeedbackDialog — "Not for me" reason picker (snippets/favorites-feedback-dialog.liquid) */
  /* ------------------------------------------------------------------ */

  var FeedbackDialog = {
    el: null,

    init: function () {
      if (this.el) return this.el;
      var dialog = document.querySelector('[data-fav-feedback-dialog]');
      if (!dialog || typeof dialog.showModal !== 'function') return null;
      this.el = dialog;
      var self = this;
      var textarea = dialog.querySelector('textarea');
      var counter = dialog.querySelector('[data-fav-feedback-counter]');
      var save = dialog.querySelector('[data-fav-feedback-save]');
      textarea.maxLength = NOTE_MAX;

      this.refresh = function () {
        if (counter) counter.textContent = textarea.value.length + '/' + NOTE_MAX;
      };
      textarea.addEventListener('input', this.refresh);
      dialog.querySelectorAll('input[name="fav-feedback-reason"]').forEach(function (radio) {
        radio.addEventListener('change', self.refresh);
      });
      dialog.querySelectorAll('[data-fav-feedback-cancel]').forEach(function (button) {
        button.addEventListener('click', function () { dialog.close(); });
      });
      save.addEventListener('click', function () {
        if (self.save()) dialog.close();
      });
      dialog.addEventListener('click', function (event) {
        if (event.target === dialog) dialog.close();
      });
      dialog.addEventListener('close', function () {
        var opener = self.opener;
        self.opener = null;
        delete dialog.dataset.productId;
        if (opener && opener.isConnected) opener.focus();
      });
      return dialog;
    },

    open: function (productId, title, opener) {
      var dialog = this.init();
      if (!dialog) return;
      var existing = FeedbackStore.get(productId);
      this.opener = opener || null;
      dialog.dataset.productId = String(productId);
      dialog.dataset.title = title || '';
      dialog.querySelector('[data-fav-feedback-product]').textContent = title || '';
      dialog.querySelectorAll('input[name="fav-feedback-reason"]').forEach(function (radio) {
        radio.checked = !!(existing && existing.rating === 'dislike' && existing.reason === radio.value);
      });
      var textarea = dialog.querySelector('textarea');
      textarea.value = (existing && existing.rating === 'dislike' && existing.comment) || '';
      var error = dialog.querySelector('[data-fav-feedback-error]');
      if (error) error.hidden = true;
      this.refresh();
      dialog.showModal();
      var first = dialog.querySelector('input[name="fav-feedback-reason"]:checked') || dialog.querySelector('input[name="fav-feedback-reason"]');
      if (first) first.focus();
    },

    save: function () {
      var dialog = this.el;
      var id = dialog.dataset.productId;
      if (!id) return false;
      var reason = dialog.querySelector('input[name="fav-feedback-reason"]:checked');
      var ok = FeedbackStore.set(id, {
        rating: 'dislike',
        title: dialog.dataset.title || '',
        reason: reason ? reason.value : undefined,
        comment: dialog.querySelector('textarea').value
      });
      var error = dialog.querySelector('[data-fav-feedback-error]');
      if (error) error.hidden = ok;
      if (ok) announce(themeStrings().favoritesFeedbackSaved || 'Feedback saved');
      return ok;
    }
  };

  /* ------------------------------------------------------------------ */
  /* <scent-feedback> — like / not-for-me controls on a purchased scent  */
  /* ------------------------------------------------------------------ */

  class ScentFeedback extends HTMLElement {
    connectedCallback() {
      this.productId = this.getAttribute('data-product-id');
      if (!isIntegerId(this.productId)) return;
      this.title = this.getAttribute('data-title') || '';
      this.likeBtn = this.querySelector('[data-fav-like]');
      this.dislikeBtn = this.querySelector('[data-fav-dislike]');
      this.summary = this.querySelector('[data-fav-feedback-summary]');
      this.summaryText = this.querySelector('[data-fav-feedback-text]');
      this.changeBtn = this.querySelector('[data-fav-feedback-change]');

      if (!this._bound) {
        this._bound = true;
        var self = this;
        this.likeBtn.addEventListener('click', function () {
          var current = FeedbackStore.get(self.productId);
          if (current && current.rating === 'like') {
            FeedbackStore.clear(self.productId);
            announce(themeStrings().favoritesFeedbackCleared || 'Rating cleared');
          } else {
            FeedbackStore.set(self.productId, { rating: 'like', title: self.title || '' });
            // Liking a scent also saves it as a favorite (un-liking leaves the favorite alone)
            var row = self.closest('.scent-row');
            var heart = (row && row.querySelector('favorite-button')) || document.querySelector('favorite-button[data-product-id="' + self.productId + '"]');
            if (heart && heart.snapshot && !FavoritesStore.has(self.productId)) FavoritesStore.add(heart.snapshot);
            announce(themeStrings().favoritesFeedbackSaved || 'Feedback saved');
          }
        });
        this.dislikeBtn.addEventListener('click', function () {
          var current = FeedbackStore.get(self.productId);
          if (current && current.rating === 'dislike') {
            FeedbackStore.clear(self.productId);
            announce(themeStrings().favoritesFeedbackCleared || 'Rating cleared');
          } else {
            FeedbackDialog.open(self.productId, self.title, self.dislikeBtn);
          }
        });
        if (this.changeBtn) {
          this.changeBtn.addEventListener('click', function () {
            FeedbackDialog.open(self.productId, self.title, self.changeBtn);
          });
        }
      }

      this._onChanged = this._onChanged.bind(this);
      document.documentElement.addEventListener('feedback:changed', this._onChanged);
      document.documentElement.addEventListener('favorites:changed', this._onChanged);
      this.render();
    }

    disconnectedCallback() {
      document.documentElement.removeEventListener('feedback:changed', this._onChanged);
      document.documentElement.removeEventListener('favorites:changed', this._onChanged);
    }

    _onChanged(event) {
      var id = event.detail && event.detail.id;
      if (id == null || String(id) === String(this.productId)) this.render();
    }

    render() {
      var entry = FeedbackStore.get(this.productId);
      var rating = entry ? entry.rating : 'none';

      // Once the scent is a favorite, notes can be added right here
      var scope = this.closest('.scent-row') || this;
      var noteToggle = scope.querySelector('[data-fav-note-toggle]');
      var notePreview = scope.querySelector('[data-fav-note-preview]');
      if (noteToggle) {
        var isFav = FavoritesStore.has(this.productId);
        noteToggle.hidden = !isFav;
        if (isFav) renderNotePreview(this.productId, notePreview, noteToggle);
        else if (notePreview) notePreview.hidden = true;
        if (!noteToggle._wired) {
          noteToggle._wired = true;
          var self = this;
          noteToggle.addEventListener('click', function () { NoteDialog.open(self.productId, noteToggle); });
        }
      }
      this.likeBtn.setAttribute('aria-pressed', rating === 'like' ? 'true' : 'false');
      this.dislikeBtn.setAttribute('aria-pressed', rating === 'dislike' ? 'true' : 'false');
      this.dataset.rating = rating;
      var row = this.closest('.scent-row');
      if (row) row.dataset.rating = rating;

      if (this.summary) {
        if (rating === 'dislike') {
          var reasons = themeStrings().favoritesFeedbackReasons || {};
          var text = reasons[entry.reason] || '';
          if (entry.comment) text += (text ? ' — ' : '') + entry.comment;
          this.summaryText.textContent = text || (themeStrings().favoritesFeedbackNoReason || 'No reason given');
          this.summary.hidden = false;
        } else {
          this.summary.hidden = true;
        }
      }
    }
  }

  if (!window.customElements.get('scent-feedback')) {
    window.customElements.define('scent-feedback', ScentFeedback);
  }

  /* ------------------------------------------------------------------ */
  /* Restock — Klaviyo client back-in-stock subscription for the signed-in customer */
  /* ------------------------------------------------------------------ */

  var RESTOCK_KEY = 'theme:restock-subscribed';
  var Restock = {
    enabled: function () {
      var settings = (window.themeVariables && window.themeVariables.settings) || {};
      var customer = (window.themeVariables && window.themeVariables.customer) || {};
      return !!(settings.favoritesKlaviyoCompanyId && customer.loggedIn && customer.email);
    },
    _list: function () {
      try { var v = JSON.parse(localStorage.getItem(RESTOCK_KEY) || '[]'); return Array.isArray(v) ? v : []; } catch (e) { return []; }
    },
    isSubscribed: function (variantId) { return this._list().indexOf(String(variantId)) !== -1; },
    _remember: function (variantId) {
      try { var l = this._list(); l.push(String(variantId)); localStorage.setItem(RESTOCK_KEY, JSON.stringify(l.slice(-200))); } catch (e) { /* ignore */ }
    },
    subscribe: async function (variantId) {
      var settings = window.themeVariables.settings;
      var customer = window.themeVariables.customer;
      try {
        var response = await fetch('https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=' + encodeURIComponent(settings.favoritesKlaviyoCompanyId), {
          method: 'POST',
          headers: { 'Content-Type': 'application/vnd.api+json', 'revision': '2025-07-15' },
          body: JSON.stringify({
            data: {
              type: 'back-in-stock-subscription',
              attributes: { profile: { data: { type: 'profile', attributes: { email: customer.email } } }, channels: ['EMAIL'] },
              relationships: { variant: { data: { type: 'catalog-variant', id: '$shopify:::$default:::' + variantId } } }
            }
          })
        });
        if (!response.ok && response.status !== 202) return false;
        this._remember(variantId);
        return true;
      } catch (e) { return false; }
    }
  };

  /* ------------------------------------------------------------------ */
  /* <favorites-page>                                                    */
  /* ------------------------------------------------------------------ */

  var CHUNK_SIZE = 50;
  var UNDO_MS = 6000;

  function pickVariant(product) {
    // Kit-builder rule: first available variant whose title contains "2 oz"; fallback first available.
    var variants = Array.isArray(product.variants) ? product.variants : [];
    var twoOz = variants.find(function (v) { return v.available && isTwoOz(v.title); });
    var first = variants.find(function (v) { return v.available; });
    return twoOz || first || null;
  }

  function isTwoOz(title) {
    return String(title || '').toLowerCase().indexOf('2 oz') !== -1;
  }

  function liveFromProductJson(json) {
    var image = sizedImage(json.featured_image || (json.images && json.images[0]) || '');
    return {
      id: json.id,
      available: !!json.available,
      url: json.handle ? '/products/' + json.handle : '',
      image: image,
      title: json.title || '',
      variant_id: (pickVariant(json) || {}).id || null,
      variant_title: (pickVariant(json) || {}).title || '',
      retired: false, // /products/{handle}.js doesn't expose metafields
      scent_type: null
    };
  }

  class FavoritesPage extends HTMLElement {
    connectedCallback() {
      this.strings = themeStrings();
      this.listAvailable = this.querySelector('[data-fav-list="available"]');
      this.listUnavailable = this.querySelector('[data-fav-list="unavailable"]');
      this.groupAvailable = this.querySelector('[data-fav-group="available"]');
      this.groupUnavailable = this.querySelector('[data-fav-group="unavailable"]');
      this.pillsEl = this.querySelector('[data-fav-pills]');
      this.countEl = this.querySelector('[data-fav-count]');
      this.emptyEl = this.querySelector('[data-fav-empty]');
      this.contentEl = this.querySelector('[data-fav-content]');
      this.toastEl = this.querySelector('[data-fav-toast]');
      this.rowTemplate = this.querySelector('template[data-fav-row]');
      this.liveSectionId = this.getAttribute('live-section-id') || 'favorites-live';
      this.similarEl = this.querySelector('[data-fav-similar]');
      this.similarList = this.querySelector('[data-fav-similar-list]');
      this.catalog = this.loadCatalog();

      this.rendered = {};    // id -> snapshot as last rendered (for undo after un-heart)
      this.live = {};        // id -> live data (or null when probed 404 / unavailable)
      this.neutral = {};     // id -> true when availability couldn't be checked
      this.activeFilter = 'all';
      this.undo = null;

      this._onChanged = this._onChanged.bind(this);
      document.documentElement.addEventListener('favorites:changed', this._onChanged);

      this.wireTabs();
      this.wirePurchased();

      this.renderAll();
      this.enrich();
    }

    disconnectedCallback() {
      document.documentElement.removeEventListener('favorites:changed', this._onChanged);
      if (this._onFeedback) document.documentElement.removeEventListener('feedback:changed', this._onFeedback);
    }

    _onChanged(event) {
      var action = event.detail && event.detail.action;
      if (action === 'reload') {
        this.renderSoon();
        this.enrich();
      } else if (action === 'remove') {
        var removed = this.rendered[String(event.detail.id)];
        if (removed) this.showUndo(removed);
        this.renderSoon();
      } else if (action === 'add') {
        this.renderSoon();
        this.enrich(); // new row needs live availability so it lands in the right group
      } else if (action === 'note') {
        var row = this.querySelector('.scent-row[data-product-id="' + event.detail.id + '"]');
        if (row) renderNotePreview(event.detail.id, row.querySelector('[data-fav-note-preview]'), row.querySelector('[data-fav-note-toggle]'));
      }
    }

    // Every rebuild goes through here. Rebuilding rows while one of our quick-buy panels is open
    // would orphan it, so wait for it to close. (The note dialog lives outside the row list.)
    renderSoon() {
      var openPanel = this.querySelector('quick-buy-drawer[open], quick-buy-popover[open]');
      if (!openPanel) { this.renderAll(); return; }
      if (this._pendingRender) return;
      this._pendingRender = true;
      var self = this;
      openPanel.addEventListener('openable-element:close', function () {
        self._pendingRender = false;
        self.renderAll();
      }, { once: true });
    }

    /* ---- tabs ---- */

    wireTabs() {
      var tabs = Array.from(this.querySelectorAll('[data-fav-tab]'));
      if (!tabs.length) return;
      var self = this;
      tabs.forEach(function (tab, index) {
        tab.addEventListener('click', function () { self.selectTab(tab.dataset.favTab); });
        tab.addEventListener('keydown', function (event) {
          var next;
          if (event.key === 'ArrowRight') next = tabs[(index + 1) % tabs.length];
          else if (event.key === 'ArrowLeft') next = tabs[(index - 1 + tabs.length) % tabs.length];
          else if (event.key === 'Home') next = tabs[0];
          else if (event.key === 'End') next = tabs[tabs.length - 1];
          if (!next) return;
          event.preventDefault();
          self.selectTab(next.dataset.favTab);
          next.focus();
        });
      });
      var fromHash = (location.hash || '').replace('#', '');
      this.selectTab(this.querySelector('[data-fav-tab="' + fromHash + '"]') ? fromHash : 'favorites', true);
    }

    selectTab(name, silent) {
      this.querySelectorAll('[data-fav-tab]').forEach(function (tab) {
        var active = tab.dataset.favTab === name;
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        tab.tabIndex = active ? 0 : -1;
      });
      this.querySelectorAll('[data-fav-panel]').forEach(function (panel) {
        panel.hidden = panel.dataset.favPanel !== name;
      });
      if (!silent && history.replaceState) {
        history.replaceState(null, '', name === 'favorites' ? location.pathname + location.search : '#' + name);
      }
    }

    /* ---- purchased tab: count + rating filters ---- */

    wirePurchased() {
      var list = this.querySelector('[data-fav-purchased-list]');
      if (!list) return;
      var self = this;
      var rows = Array.from(list.querySelectorAll('.scent-row'));
      var countEl = this.querySelector('[data-fav-purchased-count]');
      if (countEl) {
        var tpl = rows.length === 1 ? (this.strings.favoritesPurchasedCountOne || '@count@ scent') : (this.strings.favoritesPurchasedCountOther || '@count@ scents');
        countEl.textContent = tpl.replace('@count@', rows.length);
      }
      this.feedbackFilter = 'all';
      var pills = Array.from(this.querySelectorAll('[data-feedback-filter]'));
      pills.forEach(function (pill) {
        pill.addEventListener('click', function () {
          self.feedbackFilter = pill.dataset.feedbackFilter;
          pills.forEach(function (other) {
            var active = other === pill;
            other.classList.toggle('active', active);
            other.setAttribute('aria-pressed', active ? 'true' : 'false');
          });
          self.applyFeedbackFilter();
        });
      });
      this._onFeedback = function () { self.applyFeedbackFilter(); };
      document.documentElement.addEventListener('feedback:changed', this._onFeedback);
      this.applyFeedbackFilter();

      rows.forEach(function (row) {
        var restock = row.querySelector('[data-fav-restock]');
        if (restock && restock.dataset.restockVariant && Restock.enabled()) {
          restock.hidden = false;
          self.renderRestock(restock, restock.dataset.restockVariant);
          restock.addEventListener('click', self.onRestock.bind(self, restock, restock.dataset.restockVariant));
        }
      });

      // Liquid can't tell an unlisted product from a listed one, so ask search which purchased scents are
      // listed before offering add to cart. Unlisted rows become unavailable, unlinked and not buyable.
      this.unlistedIds(rows).then(function (unlisted) {
        rows.forEach(function (row) {
          if (unlisted.has(row.dataset.productId)) { self.markUnlisted(row); return; }
          var add = row.querySelector('[data-fav-add]');
          if (!add) return;
          self.wireAddToCart(add, { id: row.dataset.productId }, {
            url: row.dataset.url || '',
            available: !!row.dataset.variantId,
            variantId: row.dataset.variantId || null,
            variantTitle: row.dataset.variantTitle || '',
            buyableCount: Number(row.dataset.buyableCount || 0)
          });
        });
      });
    }

    // IDs of rows (still in the catalog) that search doesn't return. A failed check blocks nothing.
    async unlistedIds(rows) {
      var ids = rows.filter(function (row) { return row.dataset.url; }).map(function (row) { return row.dataset.productId; }).filter(isIntegerId);
      var unlisted = new Set();
      var self = this;
      var chunks = [];
      for (var i = 0; i < ids.length; i += CHUNK_SIZE) chunks.push(ids.slice(i, i + CHUNK_SIZE));
      await Promise.all(chunks.map(async function (chunk) {
        try {
          var query = chunk.map(function (id) { return 'id:' + id; }).join(' OR ');
          var url = themeRoutes().searchUrl + '?type=product&options[unavailable_products]=last&q=' + encodeURIComponent(query) + '&section_id=' + encodeURIComponent(self.liveSectionId);
          var response = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
          if (!response.ok) return;
          var script = new DOMParser().parseFromString(await response.text(), 'text/html').querySelector('[data-favorites-live]');
          if (!script) return;
          var listed = JSON.parse(script.textContent);
          chunk.forEach(function (id) { if (!listed[id]) unlisted.add(String(id)); });
        } catch (e) { /* network / parse: leave these rows as rendered */ }
      }));
      return unlisted;
    }

    markUnlisted(row) {
      row.classList.add('oos');
      delete row.dataset.url;
      row.querySelectorAll('a.scent-img, a.scent-name').forEach(function (link) { link.removeAttribute('href'); link.removeAttribute('tabindex'); });
      var add = row.querySelector('[data-fav-add]');
      if (add) add.hidden = true;
      if (!row.querySelector('.scent-row__status')) {
        var status = document.createElement('span');
        status.className = 'scent-row__status';
        status.textContent = this.strings.favoritesUnavailable || 'Not currently available';
        var anchor = row.querySelector('.scent-row__note-wrap');
        if (anchor) anchor.insertAdjacentElement('afterend', status);
      }
    }

    applyFeedbackFilter() {
      var list = this.querySelector('[data-fav-purchased-list]');
      if (!list) return;
      var filter = this.feedbackFilter || 'all';
      list.querySelectorAll('.scent-row').forEach(function (row) {
        var rating = row.dataset.rating || 'none';
        row.hidden = filter !== 'all' && rating !== filter;
      });
    }

    /* ---- rendering ---- */

    renderAll() {
      var items = FavoritesStore.list();
      var count = items.length;


      if (this.countEl) {
        var tpl = count === 1 ? (this.strings.favoritesCountOne || '@count@ saved') : (this.strings.favoritesCountOther || '@count@ saved');
        this.countEl.textContent = tpl.replace('@count@', count);
      }

      if (!FavoritesStore.loaded) { // proxy backend still hydrating: show neither state yet
        if (this.emptyEl) this.emptyEl.hidden = true;
        if (this.contentEl) this.contentEl.hidden = true;
        this.setAttribute('aria-busy', 'true');
        return;
      }
      this.removeAttribute('aria-busy');

      if (count === 0) {
        if (this.emptyEl) this.emptyEl.hidden = false;
        if (this.contentEl) this.contentEl.hidden = true;
        return;
      }
      if (this.emptyEl) this.emptyEl.hidden = true;
      if (this.contentEl) this.contentEl.hidden = false;

      this.renderPills(items);
      this.renderRows(items);
      this.renderSimilar(items);
    }

    /* ---- "Scents like your favorites" ---- */

    loadCatalog() {
      var script = this.querySelector('script[data-fav-catalog]');
      if (!script) return [];
      try {
        return (JSON.parse(script.textContent) || []).filter(function (p) { return p && isIntegerId(p.id); });
      } catch (e) { return []; }
    }

    static tokens(notes) {
      return String(notes || '').toLowerCase().split(/[•,·|]/).map(function (t) { return t.trim(); }).filter(function (t) { return t.length > 2; });
    }

    similarScents(favorites, limit) {
      if (!this.catalog.length || !favorites.length) return [];
      var favIds = {};
      favorites.forEach(function (f) { favIds[String(f.id)] = true; });
      var profiles = favorites.map(function (f) {
        return { types: (f.scent_type || []).map(function (t) { return t.toLowerCase(); }), notes: FavoritesPage.tokens(f.scent_notes) };
      });
      var scored = [];
      this.catalog.forEach(function (c) {
        var key = String(c.id);
        if (favIds[key] || !c.available || !c.variant_id) return;
        var fb = FeedbackStore.get(key);
        if (fb && fb.rating === 'dislike') return;
        var types = (c.scent_type || []).map(function (t) { return t.toLowerCase(); });
        var notes = FavoritesPage.tokens(c.scent_notes);
        var score = 0;
        profiles.forEach(function (p) {
          var typeHits = types.filter(function (t) { return p.types.indexOf(t) !== -1; }).length;
          var union = types.length + p.types.filter(function (t) { return types.indexOf(t) === -1; }).length;
          var noteHits = notes.filter(function (n) { return p.notes.indexOf(n) !== -1; }).length;
          if (typeHits) score += typeHits / Math.max(1, union); // Jaccard: closer type sets rank higher
          if (noteHits) score += noteHits * 0.75;
        });
        // Specialty lines (pet, odor-eliminating) only when clearly relevant
        if (/\bpet\b|odor/i.test(c.title)) score *= 0.8;
        if (score > 0) scored.push({ item: c, score: score });
      });
      scored.sort(function (a, b) { return b.score - a.score || a.item.title.localeCompare(b.item.title); });
      return scored.slice(0, limit).map(function (s) { return s.item; });
    }

    renderSimilar(favorites) {
      if (!this.similarEl || !this.similarList) return;
      var picks = this.similarScents(favorites, 4);
      this.similarEl.hidden = picks.length === 0;
      this.similarList.textContent = '';
      var self = this;
      picks.forEach(function (c) {
        // Feed the row builder the catalog entry as both snapshot and live data
        self.live[c.id] = { id: c.id, available: true, url: c.url, title: c.title, image: c.image, variant_id: c.variant_id, variant_title: c.variant_title, variants: new Array(c.buyable_count || 1), retired: false, scent_type: c.scent_type || [] };
        self.similarList.appendChild(self.buildRow({
          id: c.id, handle: c.handle, title: c.title, image: c.image, scent_notes: c.scent_notes || '', scent_type: c.scent_type || [], added_at: 0
        }));
      });
    }

    typesFor(item) {
      var live = this.live[item.id];
      if (live && Array.isArray(live.scent_type) && live.scent_type.length) return live.scent_type;
      return item.scent_type || [];
    }

    renderPills(items) {
      if (!this.pillsEl) return;
      var types = new Set();
      var self = this;
      items.forEach(function (item) { self.typesFor(item).forEach(function (t) { types.add(t); }); });
      if (!types.has(this.activeFilter) && this.activeFilter !== 'all') this.activeFilter = 'all';

      this.pillsEl.textContent = '';
      var all = this.makePill('all', this.strings.favoritesFilterAll || 'All');
      this.pillsEl.appendChild(all);
      Array.from(types).sort().forEach(function (type) { self.pillsEl.appendChild(self.makePill(type, type)); });
      this.pillsEl.hidden = types.size < 2;
    }

    makePill(value, label) {
      var pill = document.createElement('button');
      pill.type = 'button';
      pill.className = 'pill' + (this.activeFilter === value ? ' active' : '');
      pill.setAttribute('aria-pressed', this.activeFilter === value ? 'true' : 'false');
      pill.dataset.filter = value;
      pill.textContent = label;
      var self = this;
      pill.addEventListener('click', function () {
        self.activeFilter = value;
        self.renderSoon();
      });
      return pill;
    }

    isAvailable(item) {
      var live = this.live[item.id];
      return !!(live && live.available);
    }

    isUnavailable(item) {
      // Only claim "unavailable" when we positively know: live says so, or probe returned 404 (live === null).
      if (this.neutral[item.id]) return false;
      if (!(item.id in this.live)) return false;
      var live = this.live[item.id];
      return live === null || !live.available;
    }

    renderRows(items) {
      var self = this;
      this.rendered = {};
      items.forEach(function (item) { self.rendered[String(item.id)] = item; });
      var filtered = items.filter(function (item) {
        return self.activeFilter === 'all' || self.typesFor(item).indexOf(self.activeFilter) !== -1;
      });

      var available = filtered.filter(function (item) { return !self.isUnavailable(item); });
      var unavailable = filtered.filter(function (item) { return self.isUnavailable(item); });

      this.fillList(this.listAvailable, available);
      this.fillList(this.listUnavailable, unavailable);

      if (this.groupAvailable) this.groupAvailable.hidden = available.length === 0;
      if (this.groupUnavailable) this.groupUnavailable.hidden = unavailable.length === 0;
      // Only show the "Available" heading when there's a second group to distinguish from.
      var availableHeading = this.groupAvailable && this.groupAvailable.querySelector('[data-fav-group-heading]');
      if (availableHeading) availableHeading.hidden = unavailable.length === 0;
    }

    fillList(listEl, items) {
      if (!listEl) return;
      listEl.textContent = '';
      var self = this;
      items.forEach(function (item) { listEl.appendChild(self.buildRow(item)); });
    }

    buildRow(item) {
      var self = this;
      this.loadedImages = this.loadedImages || {};
      var frag = this.rowTemplate.content.cloneNode(true);
      var row = frag.querySelector('.scent-row');
      var live = this.live[item.id];
      var neutral = !!this.neutral[item.id];
      var unavailable = this.isUnavailable(item);
      var s = this.strings;

      row.dataset.productId = item.id;
      row.classList.toggle('oos', unavailable);

      // Keep whichever image already finished loading for this scent, so re-renders (enrich, filters) don't
      // swap sources and flash; new images fade in once decoded.
      var img = row.querySelector('.scent-img img');
      var key = String(item.id);
      var imageUrl = this.loadedImages[key] || sizedImage((live && live.image) || item.image);
      if (img) {
        if (imageUrl) {
          img.alt = '';
          img.decoding = 'async';
          img.addEventListener('load', function () { self.loadedImages[key] = img.currentSrc || img.src; img.classList.add('is-loaded'); });
          img.src = imageUrl;
          // Already shown once (re-render after enrich/filter) or cached: show immediately, no second fade
          if (this.loadedImages[key] || (img.complete && img.naturalWidth)) { this.loadedImages[key] = imageUrl; img.classList.add('is-loaded'); }
        } else img.parentNode.classList.add('scent-img--empty');
      }

      var name = row.querySelector('.scent-name');
      if (name) name.textContent = (live && live.title) || item.title;

      var notes = row.querySelector('.scent-notes');
      if (notes) {
        notes.textContent = item.scent_notes || '';
        notes.hidden = !item.scent_notes;
      }

      var status = row.querySelector('.scent-row__status');
      if (status) {
        var statusText = '';
        if (unavailable) {
          statusText = (live && live.retired) ? (s.favoritesRetired || 'Retired') : (s.favoritesUnavailable || 'Not currently available');
        } else if (neutral) {
          statusText = s.favoritesCheckFailed || "Couldn't check availability";
        }
        status.textContent = statusText;
        status.hidden = !statusText;
        status.classList.toggle('scent-row__status--soft', neutral && !unavailable);
      }

      // No link for drafts (live === null) or unlisted products; before enrich, link by handle
      var url = (live === null || (live && live.unlisted)) ? '' : ((live && live.url) || (item.handle ? '/products/' + item.handle : ''));
      row.querySelectorAll('[data-fav-link]').forEach(function (link) {
        if (url) link.href = url; else link.removeAttribute('href');
      });

      var add = row.querySelector('[data-fav-add]');
      if (add) {
        this.wireAddToCart(add, item, {
          url: url,
          available: !!(live && live.available),
          variantId: live && live.available ? live.variant_id : null,
          variantTitle: live && live.variant_title,
          buyableCount: (live && Array.isArray(live.variants)) ? live.variants.length : 0
        });
      }

      // Heart (un-heart) — reuse <favorite-button> so state stays in sync everywhere.
      var heart = row.querySelector('favorite-button');
      if (heart) {
        heart.setAttribute('data-product-id', item.id);
        heart.setAttribute('data-handle', item.handle);
        heart.setAttribute('data-title', item.title);
        heart.setAttribute('data-image', item.image);
        heart.setAttribute('data-scent-notes', item.scent_notes);
        heart.setAttribute('data-scent-type', (item.scent_type || []).join(','));
      }

      // Note
      var noteToggle = row.querySelector('[data-fav-note-toggle]');
      var notePreview = row.querySelector('[data-fav-note-preview]');
      if (noteToggle) {
        if (FavoritesStore.has(item.id)) {
          renderNotePreview(item.id, notePreview, noteToggle);
          noteToggle.addEventListener('click', function () { NoteDialog.open(item.id, noteToggle); });
        } else {
          noteToggle.hidden = true; // suggestions aren't favorites yet
        }
      }

      // Restock alert for unavailable (not retired, not deleted) favorites
      var restock = row.querySelector('[data-fav-restock]');
      if (restock) {
        var restockVariant = unavailable && live && !live.retired && live.restock_variant_id;
        if (restockVariant && Restock.enabled()) {
          restock.hidden = false;
          this.renderRestock(restock, restockVariant);
          restock.addEventListener('click', this.onRestock.bind(this, restock, restockVariant));
        } else {
          restock.hidden = true;
        }
      }

      return frag;
    }

    // Single buyable variant → direct add; several → Focal's quick-buy; none → no button.
    wireAddToCart(add, item, buy) {
      var s = this.strings;
      if (buy.buyableCount > 1 && buy.url) {
        add.replaceWith(this.buildQuickBuy(item, buy.url));
      } else if (buy.available && buy.variantId) {
        add.hidden = false;
        add.dataset.variantId = buy.variantId;
        add.dataset.label = isTwoOz(buy.variantTitle) ? (s.favoritesAdd2oz || 'Add 2 oz') : (s.favoritesAddToCart || 'Add to cart');
        add.textContent = add.dataset.label;
        add.addEventListener('click', this.onAddToCart.bind(this, add, item));
      } else {
        add.hidden = true;
      }
    }

    buildQuickBuy(item, url) {
      var s = this.strings;
      var sep = url.indexOf('?') === -1 ? '?' : '&';
      var frag = document.createDocumentFragment();
      var self = this;
      [
        { kind: 'drawer', tag: 'quick-buy-drawer', cls: 'drawer drawer--large drawer--quick-buy hidden-phone', btnCls: 'hidden-phone' },
        { kind: 'popover', tag: 'quick-buy-popover', cls: 'popover popover--quick-buy hidden-tablet-and-up', btnCls: 'hidden-tablet-and-up' }
      ].forEach(function (cfg) {
        var id = 'fav-quick-buy-' + item.id + '-' + cfg.kind;
        var button = document.createElement('button', { is: 'toggle-button' });
        button.type = 'button';
        button.className = 'add-btn ' + cfg.btnCls;
        button.setAttribute('aria-controls', id);
        button.setAttribute('aria-expanded', 'false');
        button.textContent = s.favoritesAddToCart || 'Add to cart';
        var panel = document.createElement(cfg.tag);
        panel.id = id;
        panel.className = cfg.cls;
        panel.setAttribute('href', url + sep + 'view=quick-buy-' + cfg.kind);
        frag.appendChild(button);
        frag.appendChild(panel);
      });
      return frag;
    }

    renderRestock(button, variantId) {
      var done = Restock.isSubscribed(variantId);
      button.textContent = done ? (this.strings.favoritesRestockDone || "We'll email you when it's back") : (this.strings.favoritesRestockCta || 'Notify me when it\'s back');
      button.disabled = done;
      button.classList.toggle('added', done);
    }

    async onRestock(button, variantId, event) {
      event.preventDefault();
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      var ok = await Restock.subscribe(variantId);
      button.removeAttribute('aria-busy');
      if (ok) {
        this.renderRestock(button, variantId);
        announce(this.strings.favoritesRestockDone || "We'll email you when it's back");
      } else {
        button.disabled = false;
        announce(this.strings.favoritesRestockError || "Couldn't set that up");
        var errorEl = button.closest('.scent-row').querySelector('[data-fav-add-error]');
        if (errorEl) { errorEl.textContent = this.strings.favoritesRestockError || "Couldn't set that up — try again"; errorEl.hidden = false; }
      }
    }

    /* ---- un-heart + undo ---- */

    showUndo(snapshot) {
      if (!this.toastEl) return;
      var self = this;
      this.clearUndo();
      this.undo = { snapshot: snapshot };
      var text = this.toastEl.querySelector('[data-fav-toast-text]');
      if (text) text.textContent = (this.strings.favoritesRemovedToast || 'Removed from favorites');
      this.toastEl.hidden = false;
      this.undo.timer = setTimeout(function () { self.clearUndo(); }, UNDO_MS);

      if (!this.toastEl._pauseWired) {
        this.toastEl._pauseWired = true;
        var pause = function () { if (self.undo && self.undo.timer) { clearTimeout(self.undo.timer); self.undo.timer = null; } };
        var resume = function () { if (self.undo && !self.undo.timer) self.undo.timer = setTimeout(function () { self.clearUndo(); }, UNDO_MS); };
        this.toastEl.addEventListener('mouseenter', pause);
        this.toastEl.addEventListener('focusin', pause);
        this.toastEl.addEventListener('mouseleave', resume);
        this.toastEl.addEventListener('focusout', resume);
      }

      var undoBtn = this.toastEl.querySelector('[data-fav-undo]');
      if (undoBtn && !undoBtn._wired) {
        undoBtn._wired = true;
        undoBtn.addEventListener('click', function () {
          if (!self.undo) return;
          FavoritesStore.add(self.undo.snapshot); // snapshot carries note + original added_at
          announce(self.strings.favoritesRestored || 'Restored to favorites');
          self.clearUndo();
          self.renderSoon();
        });
      }
    }

    clearUndo() {
      if (this.undo && this.undo.timer) clearTimeout(this.undo.timer);
      this.undo = null;
      if (this.toastEl) this.toastEl.hidden = true;
    }

    /* ---- add to cart ---- */

    async onAddToCart(button, item, event) {
      event.preventDefault();
      var variantId = button.dataset.variantId;
      if (!variantId) return;
      var errorEl = button.closest('.scent-row').querySelector('[data-fav-add-error]');
      if (errorEl) errorEl.hidden = true;

      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      button.classList.add('is-loading');

      try {
        var body = new FormData();
        body.append('id', variantId);
        body.append('quantity', '1');
        body.append('sections', 'mini-cart');
        var response = await fetch(themeRoutes().cartAddUrl + '.js', {
          method: 'POST',
          body: body,
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });
        var json = await response.json();
        if (!response.ok) throw new Error(json.description || 'add failed');

        var cartResponse = await fetch(themeRoutes().cartUrl + '.js');
        var cart = await cartResponse.json();
        dispatch('cart:updated', { cart: cart });
        cart.sections = json.sections;
        dispatch('cart:refresh', {
          cart: cart,
          openMiniCart: window.themeVariables.settings.cartType === 'drawer'
        });
        button.classList.add('added');
        button.textContent = this.strings.favoritesAdded2ozDone || 'Added';
        var self = this;
        setTimeout(function () {
          button.classList.remove('added');
          button.textContent = button.dataset.label;
        }, 2000);
      } catch (e) {
        if (errorEl) errorEl.hidden = false;
      } finally {
        button.disabled = false;
        button.removeAttribute('aria-busy');
        button.classList.remove('is-loading');
      }
    }

    /* ---- enrichment ---- */

    async enrich() {
      var generation = this._enrichGeneration = (this._enrichGeneration || 0) + 1;
      var items = FavoritesStore.list();
      var live = {};
      var neutral = {};
      this.live = live;
      this.neutral = neutral;
      if (items.length === 0) return; // empty store: no network requests

      var ids = items.map(function (i) { return i.id; }).filter(isIntegerId);
      var chunks = [];
      for (var i = 0; i < ids.length; i += CHUNK_SIZE) chunks.push(ids.slice(i, i + CHUNK_SIZE));

      var self = this;
      await Promise.all(chunks.map(async function (chunk) {
        var query = chunk.map(function (id) { return 'id:' + id; }).join(' OR ');
        var url = themeRoutes().searchUrl + '?type=product&options[unavailable_products]=last&q=' + encodeURIComponent(query) + '&section_id=' + encodeURIComponent(self.liveSectionId);
        var data = null;
        try {
          var response = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
          if (!response.ok) throw new Error('search failed');
          var html = await response.text();
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var script = doc.querySelector('[data-favorites-live]');
          if (!script) throw new Error('missing live payload');
          data = JSON.parse(script.textContent);
        } catch (e) {
          chunk.forEach(function (id) { neutral[id] = true; });
          return;
        }

        var misses = [];
        chunk.forEach(function (id) {
          if (data && data[id]) live[id] = data[id];
          else misses.push(id);
        });

        await Promise.all(misses.map(async function (id) {
          var item = FavoritesStore.get(id);
          if (!item) { neutral[id] = true; return; }
          if (!item.handle) { live[id] = null; return; } // saved from an order line with no product left to probe
          try {
            var res = await fetch('/products/' + encodeURIComponent(item.handle) + '.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
            if (res.status === 404) { live[id] = null; return; } // draft / unpublished
            if (!res.ok) throw new Error('probe failed');
            // Reachable by URL but missing from search = unlisted (or hidden from this shopper):
            // show it as unavailable, with no link and no add to cart
            var probed = liveFromProductJson(await res.json());
            probed.available = false;
            probed.unlisted = true;
            probed.url = '';
            live[id] = probed;
          } catch (e) {
            neutral[id] = true;
          }
        }));
      }));

      // A newer enrich() run (or a disconnect) supersedes this one
      if (generation !== this._enrichGeneration || !this.isConnected) return;
      this.renderSoon();
    }
  }

  if (!window.customElements.get('favorites-page')) {
    window.customElements.define('favorites-page', FavoritesPage);
  }
})();
